import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, CheckCircle, HelpCircle, FileQuestion, CreditCard, Bug, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TICKET_CATEGORIES = [
  { id: "general", label: "General Question", icon: HelpCircle, color: "from-blue-500 to-cyan-500" },
  { id: "account", label: "Account Issues", icon: FileQuestion, color: "from-purple-500 to-pink-500" },
  { id: "payment", label: "Payment & Refunds", icon: CreditCard, color: "from-green-500 to-emerald-500" },
  { id: "bug", label: "Bug Report", icon: Bug, color: "from-red-500 to-orange-500" },
  { id: "feedback", label: "Feedback", icon: MessageSquare, color: "from-yellow-500 to-amber-500" },
];

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCategory) {
      toast.error("Please select a category");
      return;
    }
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/support/ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: selectedCategory,
          ...formData
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTicketId(data.ticket_id);
        setIsSubmitted(true);
        toast.success("Ticket submitted successfully!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit ticket");
      }
    } catch (error) {
      toast.error("Failed to submit ticket. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950 to-slate-950">
        {/* Header */}
        <div className="bg-slate-900/80 border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/")}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🍓</span>
              <span className="text-lg font-bold text-white">Fruit Crush</span>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-20">
          <Card className="bg-slate-800/50 border-green-500/30 p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Ticket Submitted!</h2>
            <p className="text-slate-300 mb-4">
              Thank you for contacting us. We've received your request and will get back to you shortly.
            </p>
            {ticketId && (
              <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
                <p className="text-slate-400 text-sm">Your Ticket ID</p>
                <p className="text-xl font-mono font-bold text-purple-400">{ticketId}</p>
              </div>
            )}
            <p className="text-slate-400 text-sm mb-6">
              A confirmation email has been sent to <span className="text-white">{formData.email}</span>
            </p>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate("/faq")}>
                View FAQ
              </Button>
              <Button onClick={() => navigate("/")} className="bg-purple-600 hover:bg-purple-700">
                Back to Game
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🍓</span>
            <span className="text-lg font-bold text-white">Fruit Crush</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Help Center</h1>
          <p className="text-slate-400">Submit a support ticket and we'll get back to you</p>
        </div>

        {/* Quick Links */}
        <div className="flex justify-center gap-4 mb-8">
          <Button variant="outline" onClick={() => navigate("/faq")} className="text-slate-300">
            <HelpCircle className="w-4 h-4 mr-2" /> View FAQ
          </Button>
        </div>

        {/* Ticket Form */}
        <Card className="bg-slate-800/50 border-white/10">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category Selection */}
              <div>
                <label className="block text-white font-medium mb-3">What can we help you with?</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {TICKET_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        selectedCategory === cat.id
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-white/10 bg-slate-700/30 hover:border-white/30'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${cat.color} flex items-center justify-center mx-auto mb-2`}>
                        <cat.icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-white text-sm font-medium text-center">{cat.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Your Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Email Address</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-slate-400 text-sm mb-2">Subject</label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-slate-400 text-sm mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Please describe your issue in detail..."
                  rows={6}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-3 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Submit */}
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6"
              >
                {isSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Submit Ticket
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Info */}
        <p className="text-center text-slate-500 text-sm mt-6">
          We typically respond within 24-48 hours during business days.
        </p>
      </div>
    </div>
  );
}
